/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { Community } from "@packages/sdk";
import type { SidebarProps } from "@packages/ui";
import { Button, IconButton, QRCodeIcon, radius, spacing } from "@packages/ui";

import { useCommunityInviteAction } from "./useCommunityInviteAction";

// Mirrors the transparent, half-opacity styling of `SidebarTab`, which is
// `<Link>`-only and can't host an onClick-driven modal trigger.
const buttonStyle: Interpolation = {
  ":active": { backgroundColor: "transparent !important" },
  ":focus-visible": { backgroundColor: "transparent !important", opacity: 1 },
  ":hover": { backgroundColor: "transparent !important", opacity: 1 },
  backgroundColor: "transparent !important",
  gap: spacing.ms,
  justifyContent: "flex-start",
  opacity: 0.5,
};
const iconButtonStyle: Interpolation = {
  ":active": { backgroundColor: "transparent !important" },
  ":focus-visible": { backgroundColor: "transparent !important", opacity: 1 },
  ":hover": { backgroundColor: "transparent !important", opacity: 1 },
  backgroundColor: "transparent !important",
  borderRadius: radius.m,
  opacity: 0.5,
};

export type CommunityInviteSidebarTabProps = {
  community: Community | undefined;
  state?: SidebarProps["state"];
  /**
   * Invoked before the share modal opens. The mobile nav passes its
   * menu-closing callback here so the full-screen menu overlay is dismissed
   * before the modal shows (otherwise the modal mounts behind it). Desktop
   * leaves this undefined.
   */
  onBeforeShow?: () => void;
};

/**
 * The "Invite" navigation entry for the admin dashboard. Rendered as a button
 * (not a `SidebarTab`, which only navigates) that opens the community invite
 * share modal.
 */
export const CommunityInviteSidebarTab = ({
  community,
  state = "expanded",
  onBeforeShow,
}: CommunityInviteSidebarTabProps) => {
  const t = useTranslations();
  const openInvite = useCommunityInviteAction({ community, onBeforeShow });

  if (state === "closed")
    return (
      <IconButton
        aria-label={t("community_qr_invite")}
        icon={<QRCodeIcon fill="currentColor" />}
        onClick={openInvite}
        css={iconButtonStyle}
        variant="transparentPrimary"
      />
    );

  return (
    <Button
      isFullWidth
      startIcon={<QRCodeIcon fill="currentColor" />}
      onClick={openInvite}
      css={buttonStyle}
      variant="transparentPrimary"
    >
      {t("community_qr_invite")}
    </Button>
  );
};

CommunityInviteSidebarTab.displayName = "CommunityInviteSidebarTab";
